import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { connect } from 'react-redux';
import { bool, func, object, string } from 'prop-types';
import get from 'lodash/get';
import { Loader } from '@tesla/design-system-react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import { ERROR_LOG_TYPES, logSentryError } from '../../utils/sentry';
import TCLFormHelper from '../../utils/TCLFormHelper';
import {
  TCLFormApiContextProvider,
  TCLFormStateContextProvider,
  UserGeoContextProvider,
} from '../../hooks';
import { Confirmation, Error, JobApplicationForm } from '../../components';
import { storeJob } from '../../actions';
import messages from './messages';
import style from './style.module.scss';
import genericMessages from '../../messages';

const JobApplication = ({
  apiUrl,
  formSubmissionUrl,
  geoIpUrl,
  isGeoIpEnabled,
  jobData,
  schemaApiUrl,
  storeJobDetails,
}) => {
  const intl = useIntl();
  const params = useParams();
  const { jobId } = params;
  const queryParams = new URLSearchParams(window.location.search);
  const { formatMessage } = intl;
  const [applicationType, setApplicationType] = useState(null);
  const [job, setJob] = useState(null);
  const jobTitle = job ? job.title : formatMessage(messages.DefaultJobTitle, { jobId });
  const [loadingState, setLoadingState] = useState('loading');
  const [formHelper, setFormHelper] = useState({});
  const [isFormSubmitted, setFormSubmitted] = useState(false);
  const [userGeo, setUserGeo] = useState({ countryCode: 'US' });
  const { confirmationCopy, confirmationHeading } = formHelper;
  const errorHeading = isFormSubmitted
    ? formHelper.errorHeading
    : formatMessage(genericMessages.SomethingWentWrong);
  const errorCopy = isFormSubmitted ? formHelper.errorCopy : null;

  const loadFormSchema = async (id) => {
    const url = `${schemaApiUrl}/tesla_careers_form__${id}`;
    try {
      const response = await axios.get(url);
      if (response && response.status === 200) {
        setFormHelper(new TCLFormHelper(response.data));
        setLoadingState('done');
      }
    } catch (e) {
      logSentryError('CAREERS_RETRIEVE_APPLICATION_FORM_SCHEMA_FAILURE', ERROR_LOG_TYPES.ERROR, {
        applicationType: id,
      });
      setLoadingState('error');
    }
  };

  const getUserGeoData = async () => {
    const userGeoData = {
      countryCode: 'US',
    };

    try {
      const response = await axios.get(geoIpUrl);
      if (response && response.status === 200) {
        const { data: geoData } = response;
        userGeoData.countryCode = get(geoData, 'country.iso_code', null);
      }
    } catch (e) {
      logSentryError('CAREERS_RETRIEVE_GEO_IP_FAILURE', ERROR_LOG_TYPES.ERROR);
    }

    return userGeoData;
  };

  const getApplicationType = async (id) => {
    let data = null;
    const url = `${apiUrl}/${id}`;
    const at = queryParams.get('at');

    // Check if the job is already available in the store.
    if (jobData[id]) {
      data = jobData[id];
      setJob(data);
    } else {
      // Otherwise try and load it directly via the API.
      try {
        const response = await axios.get(url);
        if (response && response.status === 200) {
          // eslint-disable-next-line prefer-destructuring
          data = response.data;
          storeJobDetails(data);
          setJob(data);
        }
      } catch (e) {
        // Log error if we have no job AND no application type was passed in .
        if (!at) {
          logSentryError('CAREERS_RETRIEVE_JOB_DETAILS_FAILURE', ERROR_LOG_TYPES.ERROR, {
            jobId: id,
          });
        }
      }
    }

    return data ? data.applicationType : at;
  };

  const onFormSubmit = (values) => {
    setLoadingState('loading');
    axios
      .post(formSubmissionUrl, values)
      .then(() => {
        setLoadingState('confirmation');
      })
      .catch((e) => {
        logSentryError('CAREERS_SUBMIT_APPLICATION_FORM_FAILURE', ERROR_LOG_TYPES.ERROR, e);
        setLoadingState('error');
      })
      .then(() => {
        setFormSubmitted(true);
      });
  };

  useEffect(() => {
    (async () => {
      if (isGeoIpEnabled && geoIpUrl) {
        const userGeoData = await getUserGeoData();
        setUserGeo(userGeoData);
      }
      const applicationTypeId = await getApplicationType(jobId);
      setApplicationType(applicationTypeId);
      await loadFormSchema(applicationTypeId);
    })();
  }, [jobId]);

  switch (loadingState) {
    case 'loading':
      return <Loader show="true" />;
    case 'error':
      return <Error heading={errorHeading} copy={errorCopy} />;
    case 'confirmation':
      return (
        <Confirmation heading={confirmationHeading} jobTitle={jobTitle} copy={confirmationCopy} />
      );
    case 'done':
    default:
      return (
        <div className={`tds-layout tds-layout--constrained ${style.FormLayout}`}>
          <div className={style.FormLayoutItem}>
            <h1 className={`tds-text--h2 ${style.headingOverride}`}>
              {formatMessage(messages.Heading)}
            </h1>
            <span className="tds-text--500">{jobTitle}</span>
            <UserGeoContextProvider value={userGeo}>
              <TCLFormApiContextProvider value={formHelper}>
                <TCLFormStateContextProvider>
                  <JobApplicationForm
                    jobId={jobId}
                    onSubmit={onFormSubmit}
                    applicationType={applicationType}
                  />
                </TCLFormStateContextProvider>
              </TCLFormApiContextProvider>
            </UserGeoContextProvider>
          </div>
        </div>
      );
  }
};

JobApplication.propTypes = {
  apiUrl: string.isRequired,
  geoIpUrl: string.isRequired,
  formSubmissionUrl: string.isRequired,
  isGeoIpEnabled: bool.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  jobData: object.isRequired,
  schemaApiUrl: string.isRequired,
  storeJobDetails: func.isRequired,
};

const mapStateToProps = (state) => ({
  apiUrl: get(state, 'app.jobUrl', null),
  formSubmissionUrl: get(state, 'app.formSubmissionUrl', null),
  geoIpUrl: get(state, 'app.geoIpUrl', null),
  isGeoIpEnabled: get(state, 'app.isGeoIpEnabled', false),
  jobData: get(state, 'jobs.data', {}),
  schemaApiUrl: get(state, 'app.formSchemaApiUrl', null),
});

const mapDispatchToProps = (dispatch) => ({
  storeJobDetails: (data) => {
    dispatch(storeJob(data));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(JobApplication);
